/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { PhotoUploadButton, ProfileImage } from '@coursera-web/profile-common';
import type { ProfilePhoto as ProfilePhotoType } from '@coursera-web/profile-common';
import * as Sentry from '@sentry/react';

import { Button, InlineNotification, Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';

import ProfilePhotoQuery from 'bundles/account-settings/components/sections/profile-photo/ProfilePhotoQuery.graphql';
import UpdateProfilePhotoMutation from 'bundles/account-settings/components/sections/profile-photo/UpdateProfilePhotoMutation.graphql';
import type { ProfilePhotoQueryQuery } from 'bundles/account-settings/components/sections/profile-photo/__generated__/ProfilePhotoQuery';
import type {
  UpdateProfilePhotoMutationMutation,
  UpdateProfilePhotoMutationMutationVariables,
} from 'bundles/account-settings/components/sections/profile-photo/__generated__/UpdateProfilePhotoMutation';
import type { SaveStatus } from 'bundles/account-settings/types/ProfilePhoto';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-settings';

const PROFILE_AVATAR_SIZE = 64;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  heading: css`
    margin: 0;
  `,
  avatarUpload: css`
    .transloadit-form {
      position: absolute;
    }
  `,
  photoRow: css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: var(--cds-spacing-200);
  `,
  photoDetails: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 240px;
    gap: var(--cds-spacing-100);
  `,
  fileInfo: css`
    margin: 0;
  `,
  buttonsRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  saveRow: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  saveButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  liveRegion: css`
    width: 100%;
    min-height: 0;

    &:not(:empty) {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
};

const ProfilePhoto = () => {
  const { data, loading } = useQuery<ProfilePhotoQueryQuery>(ProfilePhotoQuery, {
    context: { clientName: 'gatewayGql' },
  });
  const [updatePhoto] = useMutation<UpdateProfilePhotoMutationMutation, UpdateProfilePhotoMutationMutationVariables>(
    UpdateProfilePhotoMutation,
    { context: { clientName: 'gatewayGql' } }
  );

  const [pendingPhoto, setPendingPhoto] = useState<ProfilePhotoType | undefined>(undefined);
  const [savedPhoto, setSavedPhoto] = useState<ProfilePhotoType | undefined>(undefined);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const profileData = data?.LearnerProfile?.me;
  const currentPhoto = profileData?.profilePhoto;
  const fullName = profileData?.fullName || '';

  const displayedPhoto = hasChanges ? pendingPhoto : (savedPhoto ?? currentPhoto);
  const profileImage = displayedPhoto?.default || undefined;

  const handleUpload = () => {
    setIsUploading(true);
    setUploadError(null);
  };

  const handleUploadSuccess = (imgs: ProfilePhotoType) => {
    setIsUploading(false);
    setPendingPhoto(imgs);
    setHasChanges(true);
    setSaveStatus('idle');
  };

  const handleUploadError = (error: string) => {
    setUploadError(error);
    setIsUploading(false);
  };

  const handleRemovePhoto = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const emptyImage: ProfilePhotoType = { size120: '', size60: '', size24: '', default: '' };
    setPendingPhoto(emptyImage);
    setHasChanges(true);
    setSaveStatus('idle');
    setUploadError(null);
  };

  const handleSave = async () => {
    if (!pendingPhoto) return;

    setSaveStatus('saving');
    try {
      const isRemoving = pendingPhoto.default === '';
      const { data: mutationData } = await updatePhoto({
        variables: {
          input: {
            profilePhoto: isRemoving
              ? { remove: {} }
              : {
                  profilePhoto: {
                    defaultSize: pendingPhoto.default,
                    size120: pendingPhoto.size120,
                    size60: pendingPhoto.size60,
                    size24: pendingPhoto.size24,
                  },
                },
          },
        },
      });

      const result = mutationData?.LearnerProfile_PatchPersonalInformation;
      if (!result || 'errors' in result) {
        throw new Error(result ? result.errors.map((error) => error.message).join(', ') : 'Unknown error');
      }

      setSavedPhoto(result.profile?.profilePhoto ?? pendingPhoto);
      setSaveStatus('succeeded');
      setHasChanges(false);
    } catch (error) {
      Sentry.captureException(error);
      setSaveStatus('failed');
    }
  };

  if (loading) return null;

  const uploadLabel = _t('Upload image');

  return (
    <div css={styles.container} data-testid="profile-photo-section">
      <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
        {_t('Profile photo')}
      </Typography2>

      <div css={styles.photoRow}>
        <div css={styles.avatarUpload}>
          <PhotoUploadButton
            label={uploadLabel}
            onUpload={handleUpload}
            onProgress={() => {}}
            onSuccess={handleUploadSuccess}
            onError={handleUploadError}
            disabled={isUploading}
            render={(onClick) => (
              <ProfileImage onClick={onClick} size={PROFILE_AVATAR_SIZE} image={profileImage} fullName={fullName} />
            )}
          />
        </div>

        <div css={styles.photoDetails}>
          <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.fileInfo}>
            {_t('Maximum size: 1MB. Supported formats: JPG, GIF, or PNG.')}
          </Typography2>

          <div css={styles.buttonsRow}>
            <PhotoUploadButton
              label={uploadLabel}
              onUpload={handleUpload}
              onProgress={() => {}}
              onSuccess={handleUploadSuccess}
              onError={handleUploadError}
              disabled={isUploading}
              render={(onClick) => (
                <Button
                  variant="secondaryNeutral"
                  size="small"
                  onClick={onClick}
                  disabled={isUploading}
                  loading={isUploading}
                >
                  {uploadLabel}
                </Button>
              )}
            />

            {!!profileImage && (
              <Button
                trackingName="profile_image_remove_button"
                withVisibilityTracking={false}
                requireFullyVisible={false}
                component={TrackedButton}
                variant="ghostSecondary"
                size="small"
                onClick={handleRemovePhoto}
                aria-label={_t('Remove photo')}
                disabled={isUploading}
              >
                {_t('Remove photo')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {isUploading && _t('Uploading photo...')}
        {!isUploading && hasChanges && pendingPhoto?.default && _t('Photo uploaded successfully.')}
      </VisuallyHidden>

      <div css={styles.saveRow}>
        <div css={styles.liveRegion} role="alert" aria-live="assertive" aria-atomic="true">
          {!!uploadError && <InlineNotification severity="error">{uploadError}</InlineNotification>}
          {saveStatus === 'failed' && (
            <InlineNotification severity="error">
              {_t('There was an error saving your profile photo. Please try again.')}
            </InlineNotification>
          )}
        </div>
        <div css={styles.liveRegion} role="status" aria-live="polite" aria-atomic="true">
          {saveStatus === 'succeeded' && (
            <InlineNotification severity="success">{_t('Your changes have been saved.')}</InlineNotification>
          )}
        </div>
        <Button
          variant="primary"
          loading={saveStatus === 'saving'}
          disabled={!hasChanges || saveStatus === 'saving'}
          onClick={handleSave}
          type="button"
          css={styles.saveButton}
        >
          {_t('Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default ProfilePhoto;
